import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { F1065PageOneService } from './f1065-page-one.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';

@Component({
    selector: 'gtw-f1065-page-one',
    templateUrl: './f1065-page-one.component.html',
    styleUrls: ['./f1065-page-one.component.scss']
    // Note: Removed OnPush for debugging - can add back later if needed
    ,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065PageOneComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  // Component State
  showFormLoader: boolean = true;
  minimise: boolean = false;
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;

  // Data
  title: string = '';
  modalTitle: string = '1065 Page 1';
  entityList: { key: string; value: any }[] = [];
  formRows: any[] = [];
  originalFormRows: any[] = [];
  codeMastMap: any[] = [];

  // Config
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  processName: string = '1065 Page 1';
  scenarioLabel: string = '';
  taxYear: number = 0;
  
  // Dynamic row indices for banking/refund rows and Yes/No row
  routingNumberIndex: number = -1;
  typeRowIndex: number = -1;
  accountNumberIndex: number = -1;
  yesNoIndex: number = -1;

  // Initialization gate (SuperPrompt V9)
  private readonly subs = new Subscription();
  private isInitialized: boolean = false;
  private isLoading: boolean = false;

  constructor(
    private f1065PageOneService: F1065PageOneService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ========== INITIALIZATION (SuperPrompt V9) ==========

  private tryInit(): void {
    if (Object.keys(this.GENERAL_CONFIG || {}).length > 0 &&
        Object.keys(this.filterParams || {}).length > 0 &&
        Object.keys(this.globalService || {}).length > 0 &&
        Object.keys(this.rowData || {}).length > 0 &&
        !this.isInitialized &&
        !this.isLoading) {
      this.isLoading = true;
      this.init();
    }
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.taxYear = this.rowData.TAX_YEAR;
    this.scenarioLabel = this.filterParams?.label || this.globalService?.globalParams?.scenario_desc || '';
    this.processName = this.globalService?.processName || '1065 Textual Information';

    if (this.filterParams?.parent_me_string) {
      this.globalService.globalParams.parent_me_string = this.filterParams.parent_me_string;
    }
  }

  private init(): void {
    this.isInitialized = true;
    this.setupEntityData();
    this.loadCodeMastDetails();
  }

  private setupEntityData(): void {
    this.entityList = [
      { key: 'Tax Year', value: this.rowData.TAX_YEAR },
      { key: 'LEID', value: this.rowData.LEID },
      { key: 'LE Name', value: this.rowData.LE_NAME }
    ];

    this.title = this.modalTitle + ' ( Tax Year : ' + this.rowData.TAX_YEAR + ' - Scenario : ' + this.scenarioLabel + ' )';
  }

  // ========== DATA LOADING ==========

  private loadCodeMastDetails(): void {
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    
    this.f1065PageOneService.loadCodeMastDetails(
      this.GENERAL_CONFIG.base_url,
      ssoId,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.codeMastMap = response.jsonObject || [];
        }
        this.loadFormData();
      },
      (error: any) => {
        console.error('Error loading code mast details:', error);
        this.loadFormData();
      }
    );
  }

  private loadFormData(): void {
    const params = {
      action_id: 31259,
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.rowData.SCENARIO_KEY,
      jcd_key: this.rowData.JCD_KEY,
      combination_key: this.rowData.COMBINATION_KEY,
      schedule_name: 'NA',
      part_no: 'NA'
    };

    this.f1065PageOneService.loadFormData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        console.log('Form data loaded:', response);
        this.processFormData(response);
        this.showFormLoader = false;
        this.isLoading = false;
        console.log('formRows length:', this.formRows.length);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading form data:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this._snackBar.open('Error loading form data', 'Close', { duration: 4000 });
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    );
  }

  private processFormData(data: any): void {
      if (!data?.irsForm?.formItemList) {
        console.error('No form data received');
        return;
      }

      // NOTE: Do NOT override title here - keep the one set in setupEntityData()
      this.formRows = [];

    // Form row index of the Yes/No line (must match yesNoIndex below; exclude from parseInt)
    const yesNoRowIndex =
      this.taxYear <= 2022 ? 57 : this.taxYear < 2025 ? 59 : 62;

    for (let i = 0; i < data.irsForm.formItemList.length; i++) {
      const formItem = data.irsForm.formItemList[i];
      const rowDimensions = formItem.dataRowList[0].rowDimensions.object_id.split('~');
      const dataCellList = formItem.dataRowList[0].dataCellList;

      const tempDataObj: any = {
          saveParams: rowDimensions,
          line_order: rowDimensions[8]
      };

      for (let j = 0; j < dataCellList.length; j++) {
          if (j === 0) {
            tempDataObj.num = dataCellList[j].name;
          } else if (j === 1) {
            tempDataObj.description = dataCellList[j].name;
          } else if (j === 2) {
            const keyParts = dataCellList[j].key.split('~');
            tempDataObj.lineAttrKeyA = keyParts[1];
            tempDataObj.attrib_name_a = keyParts[0];
            tempDataObj.attrib_order_a = keyParts[2];
            tempDataObj.is_editable = dataCellList[j].property?.is_editable_cell !== 'Y';

            // Handle different value types based on row index
            // Note: Row 4 (Business code number) is a select dropdown - keep as string for option matching
            // Note: Yes/No row holds "true"/"false" strings — never parseInt (57 / 59 / 62 by tax year)
            const isYesNoRow = i === yesNoRowIndex;
            // 2025 line 32c Type: amounts/checkboxes come from dataRowList[1]; primary cell is not a currency int
            const is2025TypeRow = this.taxYear >= 2025 && i === 60;
            if (!isYesNoRow && !is2025TypeRow && (i === 7 || i === 19 || (i >= 25 && i <= 62))) {
              tempDataObj.value1 = dataCellList[j].value == null ? 0 : parseInt(dataCellList[j].value);
            } else if (i === 4) {
              // Business code number - keep as string to match select options
              tempDataObj.value1 = dataCellList[j].value || '';
            } else if (i === 0 || i === 1 || i === 6) {
              // Date fields - store as YYYY-MM-DD string to avoid timezone shift
              if (dataCellList[j].value == null || dataCellList[j].value === '') {
                tempDataObj.value1 = '';
              } else {
                const localDate = parseAsLocalDate(dataCellList[j].value);
                if (localDate) {
                  const y = localDate.getFullYear();
                  const m = String(localDate.getMonth() + 1).padStart(2, '0');
                  const d = String(localDate.getDate()).padStart(2, '0');
                  tempDataObj.value1 = `${y}-${m}-${d}`;
                } else {
                  tempDataObj.value1 = '';
                }
              }
            } else {
              tempDataObj.value1 = dataCellList[j].value;
            }
          } else {
            const keyParts = dataCellList[j].key.split('~');
            tempDataObj.lineAttrKeyB = keyParts[1];
            tempDataObj.attrib_name_b = keyParts[0];
            tempDataObj.attrib_order_b = keyParts[2];
            tempDataObj.value2 = dataCellList[j].value;
          }
        }

        // Line 32c (Type row): CHECKING/SAVING cells live in dataRowList[1] — save via standard Yes/No A/B pattern
        if (i === 60 && this.taxYear >= 2025 && formItem.dataRowList.length > 1) {
          for (let dr = 1; dr < formItem.dataRowList.length; dr++) {
            const checkingCell = formItem.dataRowList[dr].dataCellList.find(
              (c: any) => c.key.startsWith('CHECKING~')
            );
            if (checkingCell) {
              const savingCell = formItem.dataRowList[dr].dataCellList.find(
                (c: any) => c.key.startsWith('SAVING~')
              );
              const ck = checkingCell.key.split('~');
              const sk = savingCell?.key?.split('~');
              tempDataObj.description = 'Type';
              tempDataObj.attrib_name_a = ck[0];
              tempDataObj.lineAttrKeyA = ck[1];
              tempDataObj.attrib_order_a = ck[2];
              if (sk) {
                tempDataObj.attrib_name_b = sk[0];
                tempDataObj.lineAttrKeyB = sk[1];
                tempDataObj.attrib_order_b = sk[2];
              }
              // Keep saveParams from dataRowList[0] so trans_dtls_key / form_key / occurence / trans_status match the rest of the form (AngularJS parity)
              tempDataObj.value1 =
                checkingCell.value === 'true' ? true :
                savingCell?.value === 'true'  ? false : 'NA';
              break;
            }
          }
        }

        this.formRows.push(tempDataObj);
    }

    if (this.taxYear >= 2025) {
      this.routingNumberIndex = 59;
      this.typeRowIndex = 60;
      this.accountNumberIndex = 61;
    } else {
      this.routingNumberIndex = -1;
      this.typeRowIndex = -1;
      this.accountNumberIndex = -1;
    }
    this.yesNoIndex = yesNoRowIndex;

    // Set EIN from rowData
    if (this.formRows[5]) {
      this.formRows[5].value1 = this.rowData.EIN;
    }

    // Match code mast value
    if (this.codeMastMap.length > 1 && this.formRows[4]) {
      const matchingCode = this.codeMastMap.find(obj => this.formRows[4].value1 === obj.CODE_NAME);
      if (matchingCode) {
        this.formRows[4].value1 = matchingCode.CODE_NAME;
      }
    }

    // Convert Yes/No row values from string "true"/"false" to boolean
    if (this.yesNoIndex >= 0 && this.formRows[this.yesNoIndex]) {
      if (this.formRows[this.yesNoIndex].value1 === 'true') {
        this.formRows[this.yesNoIndex].value1 = true;
      } else if (this.formRows[this.yesNoIndex].value2 === 'true') {
        this.formRows[this.yesNoIndex].value1 = false;
      } else {
        this.formRows[this.yesNoIndex].value1 = 'NA';
      }
    }

      // Store original for reset
      this.originalFormRows = JSON.parse(JSON.stringify(this.formRows));
  }

  // ========== UI LOGIC ==========

  updateColumnB(cVal: string): void {
    if (cVal) {
      const matchingCode = this.codeMastMap.find(obj => obj.CODE_NAME === cVal);
      if (matchingCode && this.formRows[3]) {
        this.formRows[3].value1 = matchingCode.CODE_DESC;
      }
    } else {
      if (this.formRows[3]) {
        this.formRows[3].value1 = '';
      }
    }
    this.cdr.detectChanges();
  }

  disableOtherH(num: number): void {
    for (let i = 15; i < 18; i++) {
      if (num !== i && this.formRows[i]) {
        this.formRows[i].value1 = 'N';
      }
    }
    this.cdr.detectChanges();
  }

  fieldVisibility(index: number, state: any, name: string): void {
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.formRows[index].value1 = 'NA';
    }
    this.cdr.detectChanges();
  }

  /**
   * The amount cells are text inputs bound with ngModel, so anything the user
   * edits comes back as a string. Coerce before doing arithmetic, otherwise `+`
   * concatenates ("100" + "200" = "100200") and `<` compares lexicographically.
   */
  private amountOf(row: { value1?: string | number | null } | undefined): number {
    const parsed = parseFloat(String(row?.value1 ?? ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  // Lines 23-26 -> 27 (total balance due), then 28 (payment) against 29/30.
  computeValues2019(): void {
    if (this.formRows[53] && this.formRows[49] && this.formRows[50] && this.formRows[51] && this.formRows[52]) {
      const totalDue = this.amountOf(this.formRows[49]) +
                       this.amountOf(this.formRows[50]) +
                       this.amountOf(this.formRows[51]) +
                       this.amountOf(this.formRows[52]);
      this.formRows[53].value1 = totalDue;

      if (this.formRows[54] && this.formRows[55] && this.formRows[56]) {
        const payment = this.amountOf(this.formRows[54]);
        if (payment < totalDue) {
          this.formRows[55].value1 = totalDue - payment;
          this.formRows[56].value1 = 0;
        } else {
          this.formRows[56].value1 = payment - totalDue;
          this.formRows[55].value1 = 0;
        }
      }
    }
    this.cdr.detectChanges();
  }

  // Lines 24-27 -> 28 (total balance due), then 29 + 30 (payments) against
  // 31 (amount owed) / 32 (overpayment).
  computeValues2023(): void {
    if (this.formRows[54] && this.formRows[50] && this.formRows[51] && this.formRows[52] && this.formRows[53]) {
      const totalDue = this.amountOf(this.formRows[50]) +
                       this.amountOf(this.formRows[51]) +
                       this.amountOf(this.formRows[52]) +
                       this.amountOf(this.formRows[53]);
      this.formRows[54].value1 = totalDue;

      if (this.formRows[55] && this.formRows[56] && this.formRows[57] && this.formRows[58]) {
        const payments = this.amountOf(this.formRows[55]) + this.amountOf(this.formRows[56]);

        if (payments < totalDue) {
          this.formRows[57].value1 = totalDue - payments;
          this.formRows[58].value1 = 0;
        } else {
          this.formRows[58].value1 = payments - totalDue;
          this.formRows[57].value1 = 0;
        }
      }
    }
    this.cdr.detectChanges();
  }

  // ========== DATE HELPERS ==========
  
  onDateInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.formRows[index]) {
      this.formRows[index].value1 = input.value || '';
    }
  }

  /**
   * Convert YYYY-MM-DD string to MM/DD/YYYY for API.
   * Uses parseAsLocalDate to avoid timezone shift.
   */
  formatDateForApi(dateStr: string): string {
    if (!dateStr) return '';
    const d = parseAsLocalDate(dateStr);
    if (!d) return '';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  onDateChange(index: number, event: string): void {
    if (this.formRows[index]) {
      this.formRows[index].value1 = event ? new Date(event) : null;
      this.cdr.detectChanges();
    }
  }

  getDateValue(index: number): string {
    const date = this.formRows[index]?.value1;
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDateInputChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.formRows[index]) {
      this.formRows[index].value1 = input.value ? new Date(input.value) : null;
      this.cdr.detectChanges();
    }
  }

  // ========== CHECKBOX HELPERS ==========

  onCheckboxChange(index: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (this.formRows[index]) {
      this.formRows[index].value1 = checked ? 'Y' : 'N';
      this.cdr.detectChanges();
    }
  }

  onCheckboxChangeWithDisable(index: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (this.formRows[index]) {
      this.formRows[index].value1 = checked ? 'Y' : 'N';
      this.disableOtherH(index);
      this.cdr.detectChanges();
    }
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // ========== SAVE / RESET / CANCEL ==========

  save(): void {
    // Validate dates
	if (this.formRows[0]?.value1 && this.formRows[1]?.value1) {
	  if (this.formRows[0].value1 > this.formRows[1].value1) {
	    this.gtwSnackbar.openSnackBar('End date cannot be before Start date', 'gtw-snackbar--red', true, 4);
	    return;
	  }
	}

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.showFormLoader = true;
    this.cdr.detectChanges();

    // Format dates
    const formattedRows = JSON.parse(JSON.stringify(this.formRows));
	formattedRows[0].value1 = this.formatDateForApi(this.formRows[0]?.value1);
	formattedRows[1].value1 = this.formatDateForApi(this.formRows[1]?.value1);
	formattedRows[6].value1 = this.formatDateForApi(this.formRows[6]?.value1);

    // Clear field if condition not met
    if (formattedRows[17]?.value1 !== 'Y') {
      formattedRows[18].value1 = '';
    }

    const saveData = this.buildSaveData(formattedRows);

    this.f1065PageOneService.savePageOneForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.processName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.showFormLoader = false;

        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error saving form', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.showFormLoader = false;
        this._snackBar.open('Error saving form', 'Close', { duration: 4000 });
        this.cdr.detectChanges();
      }
    );
  }

  private buildSaveData(formattedRows: any[]): any[] {
    const tempArray: any[] = [];
    const formData = {
      addrows: [],
      editrows: [] as any[],
      removerows: []
    };

    for (let i = 0; i < formattedRows.length; i++) { 
      const row = formattedRows[i];

      const formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyA,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value1 === 'NA' ? 'false' : row.value1,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);

      // Add formRowDataB if attrib_order_b exists
      if (row.attrib_order_b !== undefined) {
        const formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKeyB,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: row.value1 === true ? 'false' : row.value1 === 'NA' ? 'false' : row.value1 === false ? 'true' : 'true',
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }

    formData.editrows.push(tempArray);
    return [formData];
  }

  reset(): void {
    this.formRows = JSON.parse(JSON.stringify(this.originalFormRows));
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

